import { useModal } from "@/Contexts";
import { useGlobalAlert } from "@/States/globalAlert";
import {
  type ColumnType,
  checkboxView,
  textView,
} from "@/Utils/tableColumns/functions";
import { trpc } from "@/Utils/trpc";
import { useConfig } from "@/api/hooks/useConfig";
import { Table } from "@/components/Table";
import { Button } from "@/components/button/index";
import { Card, CardComponentVariant } from "@/components/pageCards/card";
import type { Person } from "@/pages/customer/components/CustomerDrawer";
import { Customer } from "@/pages/customer/manage/types";
import { useTrans } from "@/translations";

import { FC, useCallback, useMemo } from "react";

import { EmailJobType } from "server/config-api/index.js";

import { PeopleModal } from "./PeopleModal";

export type CustomerWithPeople = Customer & {
  contactPeople?: string;
};

export type ReportJob = { id: number; email: string };

interface PeopleCardProps {
  customer: CustomerWithPeople;
  onUpdate: () => void;
  reportJobs: ReportJob[];
  terminalId?: string;
}

export const PeopleCard: FC<PeopleCardProps> = ({
  customer,
  onUpdate,
  reportJobs,
  terminalId,
}) => {
  const { isMWM } = useConfig();
  const { t } = useTrans();
  const { showModal } = useModal();
  const { setGlobalAlert } = useGlobalAlert();

  const receivesActionReport = useCallback(
    (email: string) => reportJobs.findIndex((j) => j.email === email) >= 0,
    [reportJobs],
  );

  const mainPerson = customer?.contactEmail
    ? {
        name: customer?.contactPerson ?? t("drawerLabels:mainContact"),
        email: customer?.contactEmail,
        phone: customer?.contactMobile,
        sendTenantActionReport: receivesActionReport(customer?.contactEmail),
      }
    : null;
  let people = JSON.parse(customer?.contactPeople ?? "[]") as Person[];
  if (mainPerson) {
    people = [
      mainPerson,
      ...people.map((p) => ({
        ...p,
        sendTenantActionReport: receivesActionReport(p?.email),
      })),
    ];
  }

  const { mutate: createEmailJob } = trpc.config.createEmailJob.useMutation();
  const { mutate: deleteEmailJob } = trpc.config.deleteEmailJob.useMutation();

  const showEditModal = () => {
    showModal(
      <PeopleModal
        customerId={customer.id}
        people={people}
        onConfirm={onUpdate}
        reportJobs={reportJobs}
      />,
    );
  };

  const changeActionReportRecipient = useCallback(
    (value: boolean, rowData: { [k: string]: any }) => {
      const existingJob = reportJobs.find(
        (j) => j.email.toLowerCase() === rowData.email.toLowerCase(),
      );

      if (existingJob) {
        deleteEmailJob(
          { emailJobId: existingJob.id.toString() },
          {
            onSuccess: () => {
              setGlobalAlert({
                type: "success",
                message: "systemMessages:tenantActionReportRecipientDeleted",
              });
              onUpdate();
            },
          },
        );
      } else {
        createEmailJob(
          {
            terminalId: terminalId!,
            type: isMWM
              ? EmailJobType.ReportHouseholdCustomers
              : EmailJobType.TenantActionReport,
            email: rowData.email,
            tenantId: customer.id,
          },
          {
            onSuccess: () => {
              setGlobalAlert({
                type: "success",
                message: "systemMessages:tenantActionReportRecipientAdded",
              });
              onUpdate();
            },
          },
        );
      }
    },
    [
      reportJobs,
      deleteEmailJob,
      createEmailJob,
      onUpdate,
      terminalId,
      customer,
      setGlobalAlert,
      isMWM,
    ],
  );

  const columns: ColumnType[] = useMemo(
    () => [
      {
        label: "formLabels:name",
        name: "name",
        getView: textView,
      },
      {
        label: "formLabels:email",
        name: "email",
        getView: textView,
      },
      {
        label: "formLabels:phoneNumber",
        name: "phone",
        getView: textView,
      },
      {
        label: "formLabels:sendTenantActionReport",
        name: "sendTenantActionReport",
        getView: checkboxView,
        headerTooltipText: "hints:tenantActionReport",
        onChange: changeActionReportRecipient,
      },
    ],
    [changeActionReportRecipient],
  );

  return (
    <Card
      title="drawerLabels:contactPersons"
      variant={CardComponentVariant.DRAWER}
    >
      <Table
        limitedHeight
        columns={columns}
        data={people}
        sortable={false}
        emptyViewType="noContactPerson"
        emptyViewClassName="bg-grey1 mt-0"
      />
      <Button
        label={"actions:edit"}
        onClick={showEditModal}
        color="secondary"
        className="float-right mt-10"
      />
    </Card>
  );
};
